import { TDR } from 'tdr-common';

export interface TableCategories {
	available: TDR.Table[];
	dateConflict: TDR.Table[];
	timeConflict: TDR.Table[];
	availableAtOtherTimes: TDR.Table[];
	dateSoldOut: TDR.Table[];
	dateClosed: TDR.Table[];
}

export interface TableCategoryCounts {
	available: number;
	dateConflict: number;
	timeConflict: number;
	dateSoldOut: number;
	dateClosed: number;
}

export type TableSearchResultMap =
	| Record<string, TDR.Availability.searchResult>
	| Map<string, TDR.Availability.searchResult>;

/**
 * `resultMap` is typed as a Map but the service builds it with bracket assignment and it reaches
 * us as a plain object through JSON, so `.get()` is not safe to assume. Handle both shapes.
 */
export function getSearchResult(
	resultMap: TableSearchResultMap | undefined,
	tableId: string | undefined
): TDR.Availability.searchResult | undefined {
	if (!resultMap || !tableId) {
		return undefined;
	}
	if (resultMap instanceof Map) {
		return resultMap.get(tableId);
	}
	return resultMap[tableId];
}

export function categorizeTables(
	tables: TDR.Table[],
	resultMap?: TableSearchResultMap
): { categories: TableCategories; counts: TableCategoryCounts } {
	const categories: TableCategories = {
		available: [],
		dateConflict: [],
		timeConflict: [],
		availableAtOtherTimes: [],
		dateSoldOut: [],
		dateClosed: []
	};

	tables.forEach(table => {
		const result = getSearchResult(resultMap, table.id);
		const conflict = result?.conflict;
		const hasAvailableTimes = (result?.availableTimes?.length ?? 0) > 0;

		if (!conflict || conflict.length === 0) {
			categories.available.push(table);
		}
		else if (conflict.includes('date')) {
			categories.dateConflict.push(table);
			if (result?.dateStatus === 'sold_out') {
				categories.dateSoldOut.push(table);
			}
			else if (result?.dateStatus === 'closed') {
				categories.dateClosed.push(table);
			}
		}
		else if (conflict.includes('time')) {
			categories.timeConflict.push(table);
			if (hasAvailableTimes) {
				categories.availableAtOtherTimes.push(table);
			}
		}
	});

	const counts: TableCategoryCounts = {
		available: categories.available.length,
		dateConflict: categories.dateConflict.length,
		timeConflict: categories.timeConflict.length,
		dateSoldOut: categories.dateSoldOut.length,
		dateClosed: categories.dateClosed.length
	};

	return { categories, counts };
}

const MAX_SORT_ORDER = 9999;

/**
 * Tables the current search filtered out because they can't host the
 * requested party size (the treatment normally hides these entirely:
 * results.metadata.groupSize is the include-list).
 */
export function getGroupSizeConflictTables(
	tables: TDR.Table[],
	metadata?: TDR.Availability.searchOutput['metadata']
): TDR.Table[] {
	if (!metadata?.groupSize) {
		return [];
	}
	const allowed = new Set(metadata.groupSize);
	return tables
		.filter(table => !allowed.has(table.id))
		.sort((a, b) => (a.displayOrder ?? MAX_SORT_ORDER) - (b.displayOrder ?? MAX_SORT_ORDER));
}
